import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from 'react-router-dom';
import ReactDOM from "react-dom";
import LoadingBar from "react-top-loading-bar";
import { mutate } from "swr";
import { PaginationSettings } from "../../configs";
import { AdminPageTemplate } from "../../features";
import { ApiSchemaAddReforwardingVendor, ApiSchemaVendorCompanyData, Option } from '../../models';
import { useFunctionalityPagedVendorCompany, useFunctionalityVendorCompany } from "../../pages/hooks";
import { ApiDataMapper } from '../../utils/apiDataMapper';
import { ModalAddVendorCompany, ModalConfirmDelete, ModalVendorReforwardingVendor } from "../modals";
import { AssetSvg } from '../../assets';
import { VendorRoutParam, VendorConnectionType } from "../../models/api/vendor";
import { TableColumnExtend } from "../../models";
import { useParams } from "react-router-dom";

type Props = {
  parentCompanyId: string
}
export function VendorCompanyTab(props: Props): JSX.Element {
  const { parentCompanyId } = props;
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [showDeleteModal, setDeleteShowModal] = useState(false);
  const [pageIndex, setPageIndex] = useState(PaginationSettings.initialPageIndex);
  const [pageSize, setPageSize] = useState(PaginationSettings.initialPageSize);
  const [searchText, setSearchText] = useState<string>("");
  const [hasPagingData, setHasPagingData] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ApiSchemaVendorCompanyData>();
  const [showReforwardingVendorModal, setShowReforwardingVendorModal] = useState(false);
  const [selectedReforwardingRow, setSelectedReforwardingRow] = useState<ApiSchemaVendorCompanyData>();
  const [shouldEnableReforwardingAfterSave, setShouldEnableReforwardingAfterSave] = useState(false);
  const [localReforwardingVendorByCompanyId, setLocalReforwardingVendorByCompanyId] = useState<Record<string, Option>>({});

  const location = useLocation<VendorRoutParam>();
  const { vendorType } = location?.state;

  type RouteParams = {
    vendorId: string;
  };
  const { vendorId } = useParams<RouteParams>();

  const { pagedVendorCompanyList, pagedVendorCompanyListCount, pagedVendorCompanyUrl } = useFunctionalityPagedVendorCompany(
    {
      pageSize,
      pageNo: pageIndex,
      searchText: searchText?.trimStart(),
      setLoaderProgress
    }
  );

  useEffect(() => {
    if (pagedVendorCompanyListCount) {
      setHasPagingData(true);
    } else {
      setHasPagingData(false);
    }
  }, [pagedVendorCompanyListCount]);
  const {
    onDelete,
    onChangeCallForwardingOption,
    onChangeCallReForwardingOption,
    onAddReforwardingVendor,
    companyWithoutVendorUrl,
    reforwardingVendorList,
    isReforwardingVendorLoading,
  } = useFunctionalityVendorCompany({
    companyId: parentCompanyId,
    showReforwardingVendorModal
  });
  const handleSearch = (e: any) => {
    ReactDOM.unstable_batchedUpdates(() => {
      setSearchText(e.target.value);
      setPageIndex(1);
    });
  };
  const onChangePageIndex = (index: number) => {
    setPageIndex(index);
  };
  const onChangePageSize = (size: number) => {
    setPageSize(size);
  };

  const onChangeCallForwardingHandler = (row: ApiSchemaVendorCompanyData, index: number) => {
    row = { ...row, isCallForwarding: !row.isCallForwarding }
    onChangeCallForwardingOption(ApiDataMapper.VendorChangeCallForwardingOption(row))
      .then(() => {
        mutate(pagedVendorCompanyUrl)
      })
      .catch((e) => console.error(e));
  };

  const updateLocalReforwardingVendor = (companyId: string, option: Option) => {
    setLocalReforwardingVendorByCompanyId((currentState) => ({
      ...currentState,
      [companyId]: option,
    }));
  };

  const openReforwardingVendorModal = (
    row: ApiSchemaVendorCompanyData,
    enableAfterSave = false
  ) => {
    setSelectedReforwardingRow(row);
    setShouldEnableReforwardingAfterSave(enableAfterSave);
    setShowReforwardingVendorModal(true);
  };

  const onChangeCallReForwardingHandler = (
    row: ApiSchemaVendorCompanyData
  ) => {
    if (!row.isCallReForwarding) {
      openReforwardingVendorModal(row, true);
      return;
    }

    const updatedRow: ApiSchemaVendorCompanyData = {
      ...row,
      isCallReForwarding: false,
    };

    onChangeCallReForwardingOption(
      ApiDataMapper.VendorChangeCallReForwardingOption(updatedRow, vendorId!)
    )
      .then(() => {
        mutate(pagedVendorCompanyUrl);
      })
      .catch((e) => console.error(e));
  };

  const onSubmitReforwardingVendorHandler = (
    form: ApiSchemaAddReforwardingVendor
  ) => {
    const selectedVendorOption = reforwardingVendorList.find(
      (option) => option.value === form.reforwardingVendorId
    );
    const updatedSelectedRow = selectedReforwardingRow
      ? {
          ...selectedReforwardingRow,
          isCallReForwarding: shouldEnableReforwardingAfterSave
            ? true
            : selectedReforwardingRow.isCallReForwarding,
          reforwardingVendorId:
            selectedVendorOption?.value ?? form.reforwardingVendorId,
          reforwardingVendorName:
            selectedVendorOption?.label ??
            selectedReforwardingRow.reforwardingVendorName,
        }
      : undefined;

    const enableReforwardingPromise =
      shouldEnableReforwardingAfterSave && updatedSelectedRow
        ? onChangeCallReForwardingOption(
            ApiDataMapper.VendorChangeCallReForwardingOption(
              updatedSelectedRow,
              vendorId!
            )
          )
        : Promise.resolve(null);

    let hasEnabledReforwarding = false;

    enableReforwardingPromise
      .then(() => {
        hasEnabledReforwarding = shouldEnableReforwardingAfterSave;
        return onAddReforwardingVendor(form);
      })
      .then(() => {
        if (selectedVendorOption) {
          updateLocalReforwardingVendor(form.companyId, selectedVendorOption);
        }
        setSelectedReforwardingRow(updatedSelectedRow);
      })
      .then(() => {
        mutate(pagedVendorCompanyUrl);
        setShowReforwardingVendorModal(false);
        setSelectedReforwardingRow(undefined);
        setShouldEnableReforwardingAfterSave(false);
      })
      .catch((e) => {
        if (
          hasEnabledReforwarding &&
          updatedSelectedRow &&
          shouldEnableReforwardingAfterSave
        ) {
          const rollbackRow: ApiSchemaVendorCompanyData = {
            ...updatedSelectedRow,
            isCallReForwarding: false,
          };

          onChangeCallReForwardingOption(
            ApiDataMapper.VendorChangeCallReForwardingOption(
              rollbackRow,
              vendorId!
            )
          ).catch((rollbackError) => console.error(rollbackError));
        }

        console.error(e);
      });
  };

  const vendorCompanyTableData = useMemo(() => {
    return (pagedVendorCompanyList || []).map((row) => {
      const localReforwardingVendor = localReforwardingVendorByCompanyId[row.id];
      return {
        ...row,
        reforwardingVendorId:
          localReforwardingVendor?.value ?? row.reforwardingVendorId,
        reforwardingVendorName:
          localReforwardingVendor?.label ?? row.reforwardingVendorName,
      };
    });
  }, [localReforwardingVendorByCompanyId, pagedVendorCompanyList]);

  const getReforwardingVendorButtonStyle = (isDisabled: boolean) => ({
    opacity: isDisabled ? 0.18 : 1,
    cursor: isDisabled ? "not-allowed" : "pointer",
  });
  const deleteHandler = () => {
    if (selectedRow) {
      onDelete(selectedRow.id)
        .then(() => {
          mutate(pagedVendorCompanyUrl)
          mutate(companyWithoutVendorUrl)
          setDeleteShowModal(false);
        })
        .catch((e) => console.error(e));
    }
  };

  const vendorsCompanyTableColumns = [
    {
      name: "Company",
      selector: (row: ApiSchemaVendorCompanyData) => row.name,
    },
    {
      name: "Code",
      selector: (row: ApiSchemaVendorCompanyData) => row.code,
    },
    {
      name: "Call Forwarding",
      selector: (row: ApiSchemaVendorCompanyData, index: any) => (
        <input
          type="checkbox"
          checked={row.isCallForwarding}
          onChange={() => onChangeCallForwardingHandler(row, index)}
        />
      )
    },
    ...(vendorType === VendorConnectionType.SipUrl
      ? [
        {
          name: "Call Reforwarding",
          cell: (row: ApiSchemaVendorCompanyData) => (
            <input
              type="checkbox"
              checked={row.isCallReForwarding}
              onChange={() => onChangeCallReForwardingHandler(row)}
            />
          ),
        } as TableColumnExtend<ApiSchemaVendorCompanyData>,
        {
          name: "Reforwarding Vendor",
          allowOverflow: true,
          cell: (row: ApiSchemaVendorCompanyData) => {
            const isDisabled = !row.isCallReForwarding;
            const vendorName = row.reforwardingVendorName || "-";

            return (
              <div className="d-flex align-items-center">
                <span
                  style={{
                    color: isDisabled ? "#6c757d" : undefined,
                    opacity: isDisabled ? 0.35 : 1,
                    marginRight: "0.5rem",
                  }}
                >
                  {vendorName}
                </span>
                <button
                  type="button"
                  className="btn btn-link svg-color px-0"
                  style={getReforwardingVendorButtonStyle(isDisabled)}
                  disabled={isDisabled}
                  onClick={() => openReforwardingVendorModal(row)}
                  aria-label="Edit reforwarding vendor"
                >
                  <span>
                    <AssetSvg.Edit />
                  </span>
                </button>
              </div>
            );
          },
        } as TableColumnExtend<ApiSchemaVendorCompanyData>,
      ]
      : []),
    {
      name: "Actions",
      allowOverflow: true,
      selector: (row: ApiSchemaVendorCompanyData) => (
        <button
          className="btn btn-link svg-color py-0 "
          style={{ paddingLeft: "0px", paddingRight: "5px" }}
          onClick={() => {
            setDeleteShowModal(true); setSelectedRow(row)
          }}
        >
          <span >
            <AssetSvg.Delete />
          </span>
        </button>
      )
    },
  ];

  return (
    <>
      <LoadingBar
        progress={loaderProgress}
        onLoaderFinished={() => setLoaderProgress(0)}
      />
      <div className="col-12 my-3 px-3">
        <button
          className="btn btn-primary text-uppercase"
          onClick={() => setShowModal(true)}
        >
          <i className="s7 s7-plus"></i> Add Company
        </button>
      </div>
      <div className="px-3 pb-3">
        <AdminPageTemplate
          pagingKey={`VendorCompanyTab:${vendorId ?? parentCompanyId}`}
          loaderProgress={loaderProgress}
          tableHeaders={vendorsCompanyTableColumns}
          tableData={vendorCompanyTableData}
          searchText={searchText}
          handleSearch={handleSearch}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageSize={pageSize}
          count={pagedVendorCompanyListCount}
          onChangePageIndex={onChangePageIndex}
          onChangePageSize={onChangePageSize}
          hasPagingData={hasPagingData}
          setHasPagingData={setHasPagingData}
        />
      </div>
      {
        showModal && <ModalAddVendorCompany
          companyId={parentCompanyId}
          show={showModal}
          handleShow={setShowModal}
          pagedVendorCompanyUrl={pagedVendorCompanyUrl}
          companyWithoutVendorUrl={companyWithoutVendorUrl}
        />
      }
      {
        showReforwardingVendorModal && selectedReforwardingRow && (
          <ModalVendorReforwardingVendor
            show={showReforwardingVendorModal}
            handleShow={(value) => {
              setShowReforwardingVendorModal(value);
              if (!value) {
                setSelectedReforwardingRow(undefined);
                setShouldEnableReforwardingAfterSave(false);
              }
            }}
            vendorOptions={reforwardingVendorList}
            isVendorOptionsLoading={isReforwardingVendorLoading}
            initialVendorId={
              localReforwardingVendorByCompanyId[selectedReforwardingRow.id]
                ?.value ?? selectedReforwardingRow.reforwardingVendorId
            }
            companyName={selectedReforwardingRow.name}
            onSubmitHandler={onSubmitReforwardingVendorHandler}
            companyId={selectedReforwardingRow.id}
            vendorId={vendorId!}
          />
        )
      }
      {
        showDeleteModal && <ModalConfirmDelete
          show={showDeleteModal}
          handleShow={setDeleteShowModal}
          deleteHandler={deleteHandler}
          itemName={selectedRow?.name}
        />
      }
    </>
  );
}
